import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

const ABtesting = () => {
  const containerRef = useRef();
  // Setup GSAP ScrollTrigger animations
  useEffect(() => {
    if (!containerRef.current) return;

    const triggers = []; // Store our specific triggers

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      // Set initial state for all animated elements
      const animatedElements = containerRef.current.querySelectorAll('.card-animation');
      
      if (animatedElements.length === 0) return;

      gsap.set(animatedElements, {
        opacity: 0,
        y: 30
      });

      // Create ScrollTrigger for each animated element
      animatedElements.forEach((element, index) => {
        const trigger = ScrollTrigger.create({
          trigger: element,
          start: "top 90%",
          end: "bottom 10%",
          onEnter: () => {
            gsap.to(element, {
              opacity: 1,
              y: 0,
              duration: 1.2,
              delay: index * 0.2,
              ease: "power2.out"
            });
          },
          onLeave: () => {
            gsap.to(element, {
              opacity: 0,
              y: 30,
              duration: 0.8,
              ease: "power2.in"
            });
          },
          onEnterBack: () => {
            gsap.to(element, {
              opacity: 1,
              y: 0,
              duration: 1.2,
              delay: index * 0.2,
              ease: "power2.out"
            });
          },
          onLeaveBack: () => {
            gsap.to(element, {
              opacity: 0,
              y: 30,
              duration: 0.8,
              ease: "power2.in"
            });
          }
        });
        triggers.push(trigger);
      });

      // Refresh ScrollTrigger to recalculate positions
      ScrollTrigger.refresh();
    }, 100);

    return () => {
      clearTimeout(timer);
      // Only kill our specific triggers
      triggers.forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div className="container my-3 py-3 pb-5 mb-5" ref={containerRef}>
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10">
          {/* Header */}
          <div className="text-center pb-4">
            <h4 className="mb-2 f-uc">
              Find more winners
            </h4>
            <p className="f-b mb-4">
              A/B test different models to improve conversion
            </p>
          </div>

          {/* A/B Test Grid */}
          <div className="row user-select-none justify-content-center align-items-start">
            {/* Variant A */}
            <div className="col-6 col-md-3 card-animation">
              <div className="card h-100 shadow product-card o-c0">
                <div className="card-body p-0">
                  {/* Product Image */}
                  <div className="product-image-container">
    <img 
      src="img/abtest/a.png" 
      alt="Model A wearing ITORA Top" 
      className="product-image"
      loading="lazy"
    />
                  </div>
                  
                  {/* Product Details */}
                  <div className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-0">
                      <h6 className="mb-0 p-0 f-a c-c2 f-a fs-8">AROTI SHADES</h6>
                      <div className="text-end">
                        <div className="fs-6 mb-0 f-a fs-8" style={{color: 'var(--c2)'}}>$368</div>
                      </div>
                    </div>
                    
                    {/* Rating */}
                    <div className="mb-0">
                      <div className="d-flex align-items-center mb-0">
                        <div className="me-1 fs-8" style={{color: 'var(--c2)'}}>
                          ★★★★★
                        </div>
                        <small className="f-b fs-9" style={{color: 'var(--c2)'}}>(23)</small>
                      </div>
                      {/* <small className="fw-semibold" style={{color: 'var(--c00)'}}>In Stock</small> */}
                    </div>
                    
                    {/* Size and Color Selectors */}
                    <div className="mb-1 py-1">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <label className="form-label fw-semibold mb-1 f-b small c-c2 fs-9">Size</label>
                          <div className="d-flex gap-1 flex-wrap">
                            {['149', '152'].map(size => (
                              <div key={size} className="btn btn-sm size-btn-disabled fs-10 f-b o-c00 c-c2">
                                {size}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="form-label fw-semibold mb-1 f-b small c-c2 fs-9">Color</label>
                          <div className="d-flex gap-1 flex-wrap">
                            <div className="btn btn-sm size-btn-disabled fs-10 f-b o-c00 c-c2" style={{backgroundColor: 'var(--c2)'}}>&nbsp;</div>
                            <div className="btn btn-sm size-btn-disabled fs-10 f-b o-c00 c-c2" style={{backgroundColor: 'var(--c0)'}}>&nbsp;</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Add to Cart Button */}
                    <div className="btn w-100 py-1 mb-1 disabled f-b btn-sm o-c00 c-c2 f-a fs-8">
                      <i className=""></i>
                      ADD TO CART
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="col-12 col-md-1 d-none d-md-flex align-self-center justify-content-center card-animation">
              <div className="vertical-divider">
                <div className="divider-dots">
                  {Array.from({length: 10}).map((_, i) => (
                    <div className="divider-dot" key={i} />
                  ))}
                </div>
                <div className="divider-text fs-9">VS</div>
                <div className="divider-dots">
                  {Array.from({length: 10}).map((_, i) => (
                    <div className="divider-dot" key={i} />
                  ))}
                </div>
              </div>
            </div>

            {/* Variant B */}
            <div className="col-6 col-md-3 position-relative card-animation">
              <div className="winner-card-wrapper">
                <div className="card h-100 shadow product-card" style={{overflow: 'hidden', position: 'relative'}}>
                  {/* Winner Badge - Top Left Overlay */}
                  <div style={{position: 'absolute', top: 10, left: 10, zIndex: 10}}>
                    <small className="py-1 px-2 f-b fs-9 winner-badge" style={{position: 'relative'}}>
                      WINNER
                    </small>
                  </div>
                  <div className="card-body p-0">
                    {/* Product Image */}
                    <div className="product-image-container">
    <img 
      src="img/abtest/b.png" 
      alt="Model B wearing ITORA Top" 
      className="product-image"
      loading="lazy"
    />
                    </div>
                    
                    {/* Product Details */}
                    <div className="p-3">
                      <div className="d-flex justify-content-between align-items-start mb-0">
                        <h6 className="mb-0 p-0 f-a c-c2 f-a fs-8">AROTI SHADES</h6>
                        <div className="text-end">
                          <div className="fs-6 mb-0 f-a fs-8" style={{color: 'var(--c2)'}}>$368</div>
                        </div>
                      </div>
                      
                      {/* Rating */}
                      <div className="mb-0">
                        <div className="d-flex align-items-center mb-0">
                          <div className="me-1 fs-8" style={{color: 'var(--c2)'}}>
                            ★★★★★
                          </div>
                          <small className="f-b fs-9" style={{color: 'var(--c2)'}}>(23)</small>
                        </div>
                        {/* <small className="fw-semibold" style={{color: 'var(--c00)'}}>In Stock</small> */}
                      </div>
                      
                      {/* Size and Color Selectors */}
                      <div className="mb-1 py-1">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <label className="form-label fw-semibold mb-1 f-b small c-c2 fs-9">Size</label>
                            <div className="d-flex gap-1 flex-wrap">
                              {['149', '152'].map(size => (
                                <div key={size} className="btn btn-sm size-btn-disabled fs-10 f-b o-c00 c-c2">
                                  {size}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="form-label fw-semibold mb-1 f-b small c-c2 fs-9">Color</label>
                            <div className="d-flex gap-1 flex-wrap">
                              <div className="btn btn-sm size-btn-disabled fs-10 f-b o-c00 c-c2" style={{backgroundColor: 'var(--c2)'}}>&nbsp;</div>
                              <div className="btn btn-sm size-btn-disabled fs-10 f-b o-c00 c-c2" style={{backgroundColor: 'var(--c0)'}}>&nbsp;</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Add to Cart Button */}
                      <div className="btn w-100 py-1 mb-1 disabled f-b btn-sm o-c00 c-c2 f-a fs-8">
                        <i className=""></i>
                        ADD TO CART
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Second Vertical Divider - Desktop Only */}
            <div className="col-12 col-md-1 d-none d-md-flex align-self-center justify-content-center card-animation">
              <div className="vertical-divider">
                <div className="divider-dots">
                  {Array.from({length: 10}).map((_, i) => (
                    <div className="divider-dot" key={i} />
                  ))}
                </div>
                <div className="divider-text fs-9">VS</div>
                <div className="divider-dots">
                  {Array.from({length: 10}).map((_, i) => (
                    <div className="divider-dot" key={i} />
                  ))}
                </div>
              </div>
            </div>

            {/* Variant C - Desktop Only */}
            <div className="col-12 col-md-3 d-none d-md-block card-animation">
              <div className="card h-100 shadow product-card o-c0">
                <div className="card-body p-0">
                  {/* Product Image */}
                  <div className="product-image-container">
    <img 
      src="img/abtest/c.png" 
      alt="Model C wearing ITORA Top" 
      className="product-image"
      loading="lazy"
    />
                  </div>
                  
                  {/* Product Details */}
                  <div className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-0">
                      <h6 className="mb-0 p-0 f-a c-c2 f-a fs-8">AROTI SHADES</h6>
                      <div className="text-end">
                        <div className="fs-6 mb-0 f-a fs-8" style={{color: 'var(--c2)'}}>$368</div>
                      </div>
                    </div>
                    
                    {/* Rating */}
                    <div className="mb-0">
                      <div className="d-flex align-items-center mb-0">
                        <div className="me-1 fs-8" style={{color: 'var(--c2)'}}>
                          ★★★★★
                        </div>
                        <small className="f-b fs-9" style={{color: 'var(--c2)'}}>(23)</small>
                      </div>
                      {/* <small className="fw-semibold" style={{color: 'var(--c00)'}}>In Stock</small> */}
                    </div>
                    
                    {/* Size and Color Selectors */}
                    <div className="mb-1 py-1">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <label className="form-label fw-semibold mb-1 f-b small c-c2 fs-9">Size</label>
                          <div className="d-flex gap-1 flex-wrap">
                            {['149', '152'].map(size => (
                              <div key={size} className="btn btn-sm size-btn-disabled fs-10 f-b o-c00 c-c2">
                                {size}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="form-label fw-semibold mb-1 f-b small c-c2 fs-9">Color</label>
                          <div className="d-flex gap-1 flex-wrap">
                            <div className="btn btn-sm size-btn-disabled fs-10 f-b o-c00 c-c2" style={{backgroundColor: 'var(--c2)'}}>&nbsp;</div>
                            <div className="btn btn-sm size-btn-disabled fs-10 f-b o-c00 c-c2" style={{backgroundColor: 'var(--c0)'}}>&nbsp;</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Add to Cart Button */}
                    <div className="btn w-100 py-1 mb-1 disabled f-b btn-sm o-c00 c-c2 f-a fs-8">
                      <i className=""></i>
                      ADD TO CART
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        .card-animation {
          /* GSAP will handle the animation */
        }

        .product-card {
          /* Remove hover effects */
        }
        
        .product-image-container {
          position: relative;
          overflow: hidden;
          background: var(--c1);
        }
        
        .product-image {
          width: 100%;
          height: 250px;
          object-fit: cover;
          object-position: top;
          /* Remove hover effects */
        }
        
        .size-btn-disabled {
          min-width: 30px;
          border-radius: var(--border-radius);
          pointer-events: none;
          font-size: 0.7rem;
          padding: 0.2rem 0.4rem;
        }
        
        .disabled {
          pointer-events: none;
          cursor: default;
        }

        .vertical-divider {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 300px;
        }

        .divider-dots {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          margin: 0 0 8px 0;
        }
        .divider-dot {
          width: 2px;
          height: 2px;
          border-radius: 50%;
          background: var(--c2);
        }

        .divider-text {
          border:1px solid var(--c2);
          background: var(--c0);
          color: var(--c2);
          padding: 6px 12px;
          border-radius: var(--border-radius);
          font-size: 0.8rem;
          margin: 12px 0;
          line-height: 1;
        }

        @media (max-width: 767.98px) {
          .vertical-divider {
            min-height: 60px;
            flex-direction: row;
            margin: 20px 0;
          }

          .divider-dots {
            flex-direction: row;
            gap: 4px;
            margin: 0 8px 0 0;
          }
          .divider-dot {
            width: 2px;
            height: 2px;
            border-radius: 50%;
            background: var(--c2);
          }

          .divider-text {
            margin: 0 16px;
          }
        }

        @media (min-width: 768px) {
          .col-6.col-md-3:first-child .product-card,
          .col-12.col-md-3 .product-card {
            transform: scale(0.9);
            transform-origin: center center;
          }
        }

        /* Animated gradient outline for winner card */
        .winner-card-wrapper {
          position: relative;
          padding: 1px;
          border-radius: calc(var(--border-radius) + 1px);
          border: 1px solid var(--c5);
        }

        .winner-card-wrapper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, var(--c5), white, var(--c5), white, var(--c5));
          background-size: 200% 200%;
          border-radius: calc(var(--border-radius) + 1px);
          animation: gradientShift 4s ease-in-out infinite;
          filter: blur(5px);
          z-index: -1;
        }

        .winner-card-wrapper .card {
          border: none !important;
          background: white;
          margin: 0;
        }

        /* Animated gradient badge */
        .winner-card-wrapper .winner-badge {
          background: transparent !important;
          color: var(--c0) !important;
          border-radius: var(--border-radius) !important;
          box-shadow: var(--shadow-main-box) !important;
          position: relative;
          z-index: 1;
        }

        .winner-card-wrapper .winner-badge::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, var(--c4), var(--c5));
          border-radius: var(--border-radius);
          z-index: -1;
        }

        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </div>
  );
};

export default ABtesting; 